import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX, Waves, Music2 } from "lucide-react";
import { useAudio } from "../context/AudioContext";

/* Persistent, discreet master audio control. Lives at app level so it stays
   visible and in-sync across every page. Collapsed it is a whisper-quiet pill;
   expanded it reveals a minimalist panel: master volume, mute, and the ambient
   soundscape toggle — the calm authority of a premium museum guide. */
export function SoundToggle() {
  const { t } = useTranslation();
  const { on, toggle, muted, toggleMute, masterVolume, setVolume, ducking } = useAudio();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const pct = Math.round(masterVolume * 100);
  const active = on && !muted;

  return (
    <div ref={wrapRef} className="fixed bottom-6 left-6 z-[55]" data-testid="sound-control">
      {/* Expanded panel */}
      {open && (
        <div
          data-testid="sound-panel"
          className="absolute bottom-full mb-3 left-0 w-64 rounded-xl border border-white/10 bg-black/55 backdrop-blur-2xl p-5 shadow-2xl animate-fade-in"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="font-sans text-[9px] uppercase tracking-[0.35em] text-white/50">
              {t("sound.title")}
            </span>
            <button
              onClick={toggleMute}
              data-testid="mute-toggle"
              aria-pressed={muted}
              aria-label={muted ? t("sound.unmute") : t("sound.mute")}
              className="text-white/70 hover:text-white transition-colors duration-300"
            >
              {muted ? <VolumeX className="w-4 h-4" strokeWidth={1.5} /> : <Volume2 className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>

          {/* Master volume */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-sans text-[9px] uppercase tracking-[0.25em] text-white/40">{t("sound.volume")}</span>
              <span className="font-sans text-[9px] tabular-nums text-white/50" data-testid="volume-value">{muted ? 0 : pct}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={muted ? 0 : pct}
              onChange={(e) => setVolume(Number(e.target.value) / 100)}
              data-testid="master-volume-slider"
              aria-label={t("sound.volume")}
              className="ttl-range w-full"
              style={{ "--fill": `${muted ? 0 : pct}%` }}
            />
          </div>

          {/* Ambient soundscape toggle */}
          <button
            onClick={toggle}
            data-testid="ambient-toggle"
            aria-pressed={on}
            className="w-full flex items-center justify-between rounded-lg border border-white/10 px-3 py-2.5 text-left hover:border-white/25 transition-colors duration-300"
          >
            <span className="inline-flex items-center gap-2.5 text-white/70">
              <Waves className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="font-sans text-[10px] uppercase tracking-[0.2em]">{t("sound.ambient")}</span>
            </span>
            <span className={`font-sans text-[9px] uppercase tracking-[0.25em] transition-colors duration-300 ${on ? "text-[var(--accent-bronze)]" : "text-white/35"}`}>
              {on ? t("sound.on") : t("sound.off")}
            </span>
          </button>

          {ducking && (
            <div className="mt-3 inline-flex items-center gap-2 text-white/40">
              <Music2 className="w-3 h-3" strokeWidth={1.5} />
              <span className="font-sans text-[8px] uppercase tracking-[0.25em]">{t("sound.narrating")}</span>
            </div>
          )}
        </div>
      )}

      {/* Collapsed control — refined and quiet after activation.
          Before activation it expands into a discoverable inline invitation:
          "Experience with sound" beside the speaker icon. */}
      <button
        onClick={() => {
          // First click when sound is off: activate ambient AND open the panel
          // together, so the volume slider is immediately reachable in one gesture.
          // Subsequent clicks toggle the panel open/closed as normal.
          if (!on) { toggle(); setOpen(true); }
          else setOpen((o) => !o);
        }}
        data-testid="sound-toggle"
        aria-expanded={open}
        aria-pressed={on ? undefined : false}
        aria-label={on ? t("sound.title") : t("sound.experienceInvite")}
        className={`group relative flex items-center rounded-full backdrop-blur-md text-white/70 hover:text-white transition-all duration-500 ${
          on
            ? "justify-center w-9 h-9 bg-black/25 hover:bg-black/40"
            : "gap-2.5 pl-3 pr-4 h-9 border border-white/15 bg-black/45 hover:bg-black/60 hover:border-white/25"
        }`}
      >
        {muted ? (
          <VolumeX className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
        ) : (
          <Volume2 className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
        )}
        {!on && (
          <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/75 whitespace-nowrap">
            {t("sound.experienceInvite")}
          </span>
        )}
        {active && (
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--accent-bronze)] animate-pulse" />
        )}
      </button>
    </div>
  );
}

export default SoundToggle;
